/** Dev mode → Production (create-agent-dev-tree-v2.html §2). Live values from /api/kiaan/dev/production. */
import { getDevProduction } from '../kiaanApi';
import { DevState, useDevData } from './useDevData';

function ago(iso: string | null): string {
  if (!iso) return 'not checked yet';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'not checked yet';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'checked just now';
  if (m < 60) return `checked ${m} min ago`;
  return `checked ${Math.round(m / 60)} h ago`;
}

function day(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export function Production() {
  const { data, loading, error } = useDevData(getDevProduction);
  if (!data) return <DevState loading={loading} error={error} />;
  const appNames = Object.keys(data.appVersions);
  return (
    <div className="kw-grid">
      <div className="kw-box" style={{ gridColumn: '1 / 3' }}>
        <h4>Path of a change</h4>
        <div className="kw-lane" data-testid="prod-lane">
          <div className="kw-s"><b>Agent branch</b>{data.lane?.branch ?? 'none working'}</div><i>→</i>
          <div className="kw-s"><b>Dev app (develop)</b>{data.lane?.dev ?? 'dev server'}<br />
            <span className={`kw-pill ${data.lane?.devReady ? 'kw-ok' : 'kw-no'}`} style={{ margin: '4px 0 0' }}>{data.lane?.devReady ? `${data.lane.devReady} changes ready` : 'nothing new'}</span>
          </div><i>→</i>
          <div className="kw-s"><b>Live app</b>{data.web.up ? 'up' : 'down'}<br /><span className="kw-pill kw-wt" style={{ margin: '4px 0 0' }}>waits for Ardhanex</span></div>
        </div>
      </div>
      <div className="kw-box">
        <h4>Live right now</h4>
        <div className="kw-row">Website
          <span className={`kw-pill kw-ml ${data.web.up ? 'kw-ok' : 'kw-bad'}`} data-testid="prod-web">
            {data.web.up ? '● up' : '● down'}{data.web.httpStatus ? ` · ${data.web.httpStatus}` : ''} · {ago(data.web.checkedAt)}
          </span>
        </div>
        <div className="kw-row">Web version <span className="kw-mono kw-ml">{data.lastDeploy?.webVersion ?? '—'}</span></div>
        {appNames.length === 0 && <div className="kw-row">Apps <span className="kw-small kw-ml">no versions read</span></div>}
        {appNames.map((n) => (
          <div key={n} className="kw-row">{n} <span className="kw-mono kw-ml">{data.appVersions[n]}</span></div>
        ))}
        <div className="kw-row">Last deploy <span className="kw-mono kw-ml">{data.lastDeploy ? `${day(data.lastDeploy.at)}${data.lastDeploy.by ? ` · by ${data.lastDeploy.by}` : ''}` : '—'}</span></div>
        <div className="kw-row">Backup before it <span className={`kw-pill kw-ml ${data.lastDeploy?.backupSaved ? 'kw-ok' : 'kw-no'}`}>{data.lastDeploy?.backupSaved ? 'saved' : 'unknown'}</span></div>
      </div>
      <div className="kw-box">
        <h4>Waiting to go live <span>{data.waiting.length}</span></h4>
        {data.waiting.length === 0 && <div className="kw-small">Nothing waits to go live.</div>}
        {data.waiting.map((w, i) => (
          <div key={i} className="kw-row">{w.title} <span className={`kw-pill kw-ml ${/tested/i.test(w.status) ? 'kw-ok' : 'kw-wt'}`}>{w.status}</span></div>
        ))}
        <button type="button" className="kw-btn" disabled={data.waiting.length === 0} title="Coming soon">Ask Ardhanex to go live</button>
      </div>
      <div className="kw-box">
        <h4>Last 7 days on live</h4>
        {data.week ? (
          <>
            <div className="kw-row">Uptime <span className="kw-pill kw-ok kw-ml">{data.week.uptime}</span></div>
            <div className="kw-row">Errors <span className="kw-pill kw-ok kw-ml">{data.week.errors}</span></div>
            <div className="kw-row">Down alerts <span className="kw-pill kw-ok kw-ml">{data.week.downAlerts}</span></div>
          </>
        ) : (
          <div className="kw-small">No 7-day numbers yet.</div>
        )}
      </div>
      <div className="kw-box">
        <h4>If something breaks</h4>
        <div className="kw-row">Roll back to <span className="kw-mono kw-ml">{data.rollbackPoint ?? 'no rollback point saved'}</span></div>
        <div className="kw-small">Roll back is done by Ardhanex, one click on our side.</div>
      </div>
    </div>
  );
}
